import type { LogsInitConfiguration } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { App } from 'vue';

import { getOrGenerateId } from '@/utils/correlationId';

import { version } from '../../../package.json';
import { isLocal } from './is-local';

const SERVICE = 'customer-credit-club-vue';
const HOST = location.host;

const getEnvironment = () => {
  if (isLocal()) return 'local';
  else if (HOST.includes('sit1')) return 'sit';
  else if (HOST.includes('pre1')) return 'pre';
  return 'prod';
};
const env = getEnvironment();

const options: LogsInitConfiguration = {
  clientToken: 'pub66ad9a4074f26126d6cd112770cdc5a5',
  service: SERVICE,
  env: env,
  site: 'datadoghq.eu',
  version: version,
  forwardErrorsToLogs: true,
  forwardConsoleLogs: ['error'],
  sessionSampleRate: 100,
  beforeSend: (log) => {
    // Drop fetch errors from third-party scripts (GTM, ABTasty, cookie consent, IdP)
    // that are noise — they're not errors in our application code.
    const THIRD_PARTY_DOMAINS = [
      'tags.moneysavingexpert.com',
      'abtasty.com',
      'moneysavingexpert.com/cookie-consent-v2',
      'moneysavingexpert.com/idp',
    ];

    if (
      log.http?.url &&
      THIRD_PARTY_DOMAINS.some((domain) => log.http!.url!.includes(domain))
    ) {
      return false;
    }

    try {
      log.correlationId = getOrGenerateId();
    } catch {
      // Avoid dropping the log if sessionStorage is unavailable.
    }

    try {
      if (sessionStorage.getItem('fromMse') && sessionStorage.getItem('fromMse') === 'true') {
        log.fromMse = true;
      }
    } catch {
      // Avoid dropping the log if sessionStorage is unavailable.
    }

    // Drop cross-origin "Script error." noise. Browsers mask errors from scripts
    // loaded from a different origin (iovation, ABTasty, GTM tags) as "Script error."
    // with no stack. These are never from our own code — same-origin errors always
    // include a real message and stack trace.
    if (log.error?.handling === 'unhandled' && log.message?.includes('Script error')) {
      return false;
    }

    return true;
  },
};

const initDatadog = (app: App<Element>) => {
  const TRACEABLE_PATHS = [
    '/creditclub',
    '/credit',
    '/money/broker'
  ] as const;

  const createTracingPattern = (paths: readonly string[]) => 
    new RegExp(`^https:\\/\\/[^/]+(\\/(?:${paths.join('|')}).*)?$`);

  const allowedTracingUrls = [createTracingPattern(TRACEABLE_PATHS)];

  if (!isLocal()) {
    datadogLogs.init(options);
    window.$log = datadogLogs.logger;
    
    app.config.errorHandler = (err, instance, info) => {
      window.$log.error('Vue Error Handler', { error: err, info });
    };
    
    app.config.warnHandler = (msg, instance, trace) => {
      window.$log.warn('Vue Warning Handler', { message: msg, trace });
    };

    datadogRum.init({
      applicationId: 'da6b4082-1604-400f-8b91-0140865c6d49',
      clientToken: 'pub261e2821a17fdc7485ca9bb9ba4a950c',
      site: 'datadoghq.eu',
      service: SERVICE,
      env: env,
      version: version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 1,
      defaultPrivacyLevel: 'mask',
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      allowedTracingUrls,
      enablePrivacyForActionName: true,
    });
  } else {
    /* eslint-disable no-console */
    window.$log = {
      debug: console.debug.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    };
  }
  return;
};

export { initDatadog };
